import { Component, OnInit, inject, ChangeDetectorRef, ViewChild, TemplateRef, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../core/services/settings.service';
import { Store } from '@ngrx/store';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { UserModalComponent } from '../user-modal/user-modal';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { UserTypeBadgeService } from '../../../../core/services/user-type-badge.service';
import { CredentialRevealModalComponent } from '../../../../shared/components/credential-reveal-modal/credential-reveal-modal';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-users-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppPrimeTable,
    AppDropdown,
    AppButton,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TranslateModule,
    UserModalComponent,
    HasPermissionDirective,
    CredentialRevealModalComponent,
    ConfirmDialogModule
  ],
  templateUrl: './users-tab.html',
  providers: [DatePipe, ConfirmationService]
})
export class UsersTab implements OnInit {
  private settingsService = inject(SettingsService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private datePipe = inject(DatePipe);
  private perm = inject(PermissionService);
  private confirmationService = inject(ConfirmationService);
  userTypeBadge = inject(UserTypeBadgeService);

  get canCreate(): boolean { return this.perm.canCreate(MODULE.CONFIGURACOES); }
  get canEdit(): boolean { return this.perm.canEdit(MODULE.CONFIGURACOES); }
  get canDelete(): boolean { return this.perm.canDelete(MODULE.CONFIGURACOES); }

  @ViewChild('actionsTpl', { static: true }) actionsTpl!: TemplateRef<any>;
  @ViewChild('statusTpl', { static: true }) statusTpl!: TemplateRef<any>;
  @ViewChild('userTypeTpl', { static: true }) userTypeTpl!: TemplateRef<any>;
  @ViewChild('nameTpl', { static: true }) nameTpl!: TemplateRef<any>;
  @Output() navigateToProfiles = new EventEmitter<void>();

  users: any[] = [];
  loading = true;
  searchControl = new FormControl('');
  branchId: number | null = null;
  
  // Modal state
  showUserModal = false;
  selectedUserForEdit: any = null;
  
  // Delete modal
  showDeleteModal = false;
  userToDelete: any = null;
  isDeletingUser = false;

  // Reissue temp password — credential-reveal modal state
  showCredentialModal = false;
  reissuedEmail: string | null = null;
  reissuedTempPassword: string | null = null;
  isReissuing = false;

  templates: { [key: string]: TemplateRef<any> } = {};

  columns: PrimeTableColumn[] = [
    { field: 'name', header: 'user_name', type: 'custom' },
    { field: 'email', header: 'user_email' },
    { field: 'user_type_name', header: 'user_type', type: 'custom' },
    { field: 'profile_name', header: 'user_profile' },
    { field: 'status', header: 'status', type: 'custom' },
    { field: 'created_at', header: 'created_at' },
    { field: 'actions', header: 'actions', type: 'action', width: '100px', styleClass: 'text-center' }
  ];

  ngOnInit() {
    this.templates = {
      'actions': this.actionsTpl,
      'status': this.statusTpl,
      'user_type_name': this.userTypeTpl,
      'name': this.nameTpl
    };

    this.store.select(selectBranchId).subscribe(id => {
      this.branchId = id;
      if (id) {
        this.loadData();
      }
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      this.loadData(value || '');
    });
  }

  async loadData(search: string = '') {
    if (!this.branchId) return;
    this.loading = true;
    try {
      this.users = await firstValueFrom(this.settingsService.getUsers(search, this.branchId));
      await this.loadLoginStatuses();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Whether an invited/reissued user has actually logged in and changed their
   * password yet — email delivery alone can't be trusted as proof (see
   * get_auth_login_status).  Best-effort: a failure here must not block the
   * user list itself from showing.
   */
  private async loadLoginStatuses() {
    const uuids = (this.users || []).map((u) => u.user_uuid).filter(Boolean);
    if (!uuids.length) return;
    try {
      const statuses = await firstValueFrom(this.settingsService.getLoginStatuses(uuids));
      const byUuid = new Map(statuses.map((s) => [s.user_uuid, s]));
      this.users = this.users.map((u) => ({
        ...u,
        login_state: SettingsService.loginState(byUuid.get(u.user_uuid)),
      }));
    } catch (err) {
      console.error('Error loading login statuses', err);
    }
  }

  reissuePassword(user: any) {
    if (!this.canEdit) return;
    this.confirmationService.confirm({
      message: this.translate.instant('reissue_password_confirm_message', { name: user.name }),
      header: this.translate.instant('reissue_password_confirm_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      accept: async () => {
        this.isReissuing = true;
        this.cdr.detectChanges();
        try {
          const result = await firstValueFrom(this.settingsService.reissueTempPassword(user.user_uuid));
          this.reissuedEmail = result.email;
          this.reissuedTempPassword = result.tempPassword;
          this.showCredentialModal = true;
        } catch (err) {
          console.error('Error reissuing temp password', err);
          toast.error(this.translate.instant('reissue_password_error'));
        } finally {
          this.isReissuing = false;
          this.cdr.detectChanges();
        }
      },
    });
  }

  onCredentialModalClosed() {
    this.showCredentialModal = false;
    this.reissuedEmail = null;
    this.reissuedTempPassword = null;
    // The reissued user's login_state should show "not logged in" again.
    this.loadLoginStatuses();
  }

  onAddUser() {
    if (!this.canCreate) return;
    this.selectedUserForEdit = null;
    this.showUserModal = true;
  }

  /** Clinic owner accounts (user_type_id 2) are protected from edit/delete/status changes. */
  isProtected(user: any): boolean {
    return user?.user_type_id === 2;
  }

  onEdit(user: any) {
    if (!this.canEdit) return;
    if (this.isProtected(user)) return;
    this.selectedUserForEdit = user;
    this.showUserModal = true;
  }

  openDeleteModal(user: any) {
    if (!this.canDelete) return;
    // Owners (type 2) are fully protected; doctors (type 3) can't be deleted here
    // (they must be managed from the Professionals module).
    if (this.isProtected(user) || user?.user_type_id === 3) return;
    this.userToDelete = user;
    this.showDeleteModal = true;
  }

  async onToggleStatus(user: any) {
    if (!this.canEdit) return;
    if (this.isProtected(user)) return;
    try {
      await firstValueFrom(this.settingsService.toggleUserStatus(user.id, user.status, user.user_type_id));
      toast.success(this.translate.instant('settings_user_success_update'));
      this.loadData(this.searchControl.value || '');
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('settings_user_error_update'));
    }
  }

  async confirmDelete() {
    if (!this.canDelete) return;
    if (!this.userToDelete) return;

    const user = this.userToDelete;
    this.isDeletingUser = true;
    try {
      const result = await firstValueFrom(this.settingsService.deleteUser(user.id));
      if (result?.success) {
        toast.success(this.translate.instant('settings_user_success_delete'));
        this.loadData(this.searchControl.value || '');
        this.showDeleteModal = false;
        this.userToDelete = null;
      } else if (result?.reason === 'has_linked_data') {
        toast.error(
          this.translate.instant('settings_user_delete_blocked', {
            name: user.name,
            reasons: this.formatBlockers(result.blockers),
          }),
        );
      } else if (result?.reason === 'is_doctor') {
        toast.error(this.translate.instant('settings_user_delete_is_doctor'));
      } else if (result?.reason === 'forbidden') {
        toast.error(this.translate.instant('settings_user_delete_forbidden'));
      } else {
        toast.error(this.translate.instant('settings_user_error_delete'));
      }
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('settings_user_error_delete'));
    } finally {
      this.isDeletingUser = false;
    }
  }

  /** Map blocker codes from the RPC to translated, comma-joined labels. */
  private formatBlockers(blockers: string[] | undefined | null): string {
    if (!blockers?.length) return '';
    return blockers.map((b) => this.translate.instant('blocker_' + b)).join(', ');
  }

  closeUserModal() {
    this.showUserModal = false;
    this.selectedUserForEdit = null;
  }

  onUserSaved() {
    this.loadData(this.searchControl.value || '');
    this.closeUserModal();
  }

  onNavigateToProfiles() {
    this.closeUserModal();
    this.navigateToProfiles.emit();
  }
}
